import React from 'react';
import {
  formatMoney, formatPct, deltaInfo, funnelStages, sparkSeries,
} from './cartAnalyticsFormat';
import {
  Sparkline, Donut, FunnelBars, GaugeBar, COLORS,
} from './Charts';

const DELTA_CLASS = { up: 'text-success', down: 'text-danger', flat: 'text-muted', new: 'text-info' };

const fmtNum = (v) => (v === undefined || v === null || v === '' ? '-' : Number(v).toLocaleString('en-US'));
const fmtDec = (v) => (v === undefined || v === null || v === '' ? '-' : Number(v).toFixed(1));

// One KPI tile: label, value, change vs previous period and (optionally) a sparkline.
function Tile({
  label, value, cur, prev, spark, color, hint,
}) {
  const d = deltaInfo(cur, prev);
  return (
    <div className="col-6 col-md-4 col-xl-6 mb-3">
      <div className="card ca-kpi h-100" title={hint || ''}>
        <div className="card-body">
          <div className="ca-kpi-label">{label}</div>
          <div className="ca-kpi-value">{value}</div>
          <div className={`ca-kpi-delta ${DELTA_CLASS[d.dir]}`}>
            {d.text} <span className="text-muted">vs prev</span>
          </div>
          {spark && spark.length > 1 && <div className="ca-kpi-spark"><Sparkline values={spark} color={color} /></div>}
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, tiles, side }) {
  return (
    <div className="mb-4">
      <h6 className="ca-group-title">{title}</h6>
      {subtitle && <p className="ca-group-subtitle">{subtitle}</p>}
      <div className="row">
        <div className="col-12 col-xl-7"><div className="row">{tiles}</div></div>
        <div className="col-12 col-xl-5 mb-3">{side}</div>
      </div>
    </div>
  );
}

export default function KpiCards({ current, previous, trend, ccy }) {
  if (!current) return null;
  const c = current;
  const p = previous || {};
  const s = (key) => sparkSeries(trend, key, ccy);
  const money = (v) => formatMoney(v, c.ccy);

  // Value by outcome. The API classifies every cart once, so the parts add up to the total.
  const saved = Math.max(0, Number(c.cart_value) - Number(c.completed_value) - Number(c.open_value));
  const funnel = funnelStages(c);

  return (
    <div>
      <Section
        title="Volume"
        tiles={[
          <Tile key="atc" label="Add to cart" value={fmtNum(c.atc_events)} cur={c.atc_events} prev={p.atc_events} spark={s('atc_events')} color={COLORS.blue} hint="Items still in the cart or ordered. Items removed from the cart are not counted." />,
          <Tile key="carts" label="Carts" value={fmtNum(c.carts)} cur={c.carts} prev={p.carts} spark={s('carts')} color={COLORS.teal} />,
          <Tile key="items" label="Items (qty)" value={fmtNum(c.items_qty)} cur={c.items_qty} prev={p.items_qty} spark={s('items_qty')} color={COLORS.blue} />,
          <Tile key="rep" label="Repeat customers" value={fmtNum(c.repeat_customers)} cur={c.repeat_customers} prev={p.repeat_customers} hint="Customers with more than one cart in the range" />,
          <Tile key="new" label="New sign-ups with cart" value={fmtNum(c.new_signups_with_cart)} cur={c.new_signups_with_cart} prev={p.new_signups_with_cart} hint="Customers who signed up inside the range" />,
        ]}
        side={(
          <div className="card ca-info-card h-100">
            <div className="card-body">
              <div className="ca-info-title">Who is adding to cart</div>
              <Donut
                parts={[
                  { label: 'Registered customers', value: Number(c.customers) || 0, color: COLORS.blue },
                  { label: 'Guest visitors', value: Number(c.visitors) || 0, color: COLORS.grey },
                ]}
                centerLabel={formatPct(c.registered_share)}
                centerSub="registered"
                formatValue={fmtNum}
              />
            </div>
          </div>
        )}
      />

      <Section
        title="Value"
        tiles={[
          <Tile key="val" label="Total cart value" value={money(c.cart_value)} cur={c.cart_value} prev={p.cart_value} spark={s('cart_value')} color={COLORS.green} />,
          <Tile key="avg" label="Average cart value" value={money(c.avg_cart_value)} cur={c.avg_cart_value} prev={p.avg_cart_value} />,
          <Tile key="ipc" label="Items per cart" value={fmtDec(c.avg_items_per_cart)} cur={c.avg_items_per_cart} prev={p.avg_items_per_cart} />,
          <Tile key="app" label="Average price per item" value={money(c.avg_line_price)} cur={c.avg_line_price} prev={p.avg_line_price} />,
        ]}
        side={(
          <div className="card ca-info-card h-100">
            <div className="card-body">
              <div className="ca-info-title">Cart value by outcome</div>
              <Donut
                parts={[
                  { label: 'Completed', value: Number(c.completed_value) || 0, color: COLORS.green },
                  { label: 'Incomplete', value: Number(c.open_value) || 0, color: COLORS.amber },
                  { label: 'Saved for later', value: saved, color: COLORS.grey },
                ]}
                centerLabel={formatPct(c.conversion_rate_value)}
                centerSub="completed"
                formatValue={money}
              />
            </div>
          </div>
        )}
      />

      <Section
        title="Funnel"
        subtitle="Every cart is counted once: completed, incomplete or saved for later."
        tiles={[
          <Tile key="inc" label="Incomplete carts" value={fmtNum(c.open_carts)} cur={c.open_carts} prev={p.open_carts} />,
          <Tile key="incv" label="Incomplete cart value" value={money(c.open_value)} cur={c.open_value} prev={p.open_value} spark={s('open_value')} color={COLORS.amber} />,
          <Tile key="cmp" label="Completed carts" value={fmtNum(c.completed_carts)} cur={c.completed_carts} prev={p.completed_carts} spark={s('completed_carts')} color={COLORS.green} />,
          <Tile key="cmpv" label="Completed value" value={money(c.completed_value)} cur={c.completed_value} prev={p.completed_value} />,
          <Tile key="sav" label="Saved for later" value={fmtNum(c.save_later_carts)} cur={c.save_later_carts} prev={p.save_later_carts} />,
        ]}
        side={(
          <div className="card ca-info-card h-100">
            <div className="card-body">
              <div className="ca-info-title">Carts by outcome</div>
              <FunnelBars funnel={funnel} />
              <div className="ca-gauges">
                <GaugeBar label="Conversion rate (carts)" pct={c.conversion_rate_carts} tone={COLORS.green} />
                <GaugeBar label="Abandonment rate (carts)" pct={c.abandonment_rate_carts} tone={COLORS.amber} />
                <GaugeBar label="Abandonment rate (value)" pct={c.abandonment_rate_value} tone={COLORS.red} />
              </div>
            </div>
          </div>
        )}
      />

      <Section
        title="Pricing"
        subtitle="Compared with each item's old (strike-through) price at the time it was added to the cart."
        tiles={[
          <Tile key="off" label="Lines below old price" value={formatPct(c.offer_share)} cur={c.offer_share} prev={p.offer_share} hint="Share of cart lines added at a price lower than the old price" />,
          <Tile key="dis" label="Average markdown" value={formatPct(c.avg_discount_pct)} cur={c.avg_discount_pct} prev={p.avg_discount_pct} hint="Average of (old price - price) / old price over those lines" />,
        ]}
        side={(
          <div className="card ca-info-card h-100">
            <div className="card-body">
              <div className="ca-info-title">Price position of cart lines</div>
              <div className="ca-gauges">
                <GaugeBar label="Lines below old price" pct={c.offer_share} tone={COLORS.blue} />
                <GaugeBar label="Average markdown vs old price" pct={c.avg_discount_pct} tone={COLORS.teal} />
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
}
